import {
    type FC
} from "react";
import {
    Pressable,
    View
} from "react-native";
import type ITreeViewRowProps from "./type";
import stylesheet, {
    getTreeViewRowColors,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../../../types";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    useTreeViewSelector,
    useTreeViewContext
} from "../../context";
import {
    useTreeViewRowHover
} from "../../hook";
import {
    ChevronRight as ChevronRightIcon,
    CircleAlert as CircleAlertIcon,
    RotateCcw as RotateCcwIcon
} from "lucide-react-native";
import {
    TREE_VIEW_PART_TAB_INDEX
} from "../../util";
import StateLayer from "../../../stateLayer";
import TreeViewAction from "../action";
import TreeViewToggle from "../toggle";
import Button from "../../../button";
import TreeViewCheck from "../check";
import Icon from "../../../icon";
import Text from "../../../text";

const TreeViewRetryIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => {
    return <RotateCcwIcon
        color={customColor}
        size={size}
    />;
};

const TreeViewRow: FC<ITreeViewRowProps> = ({
    isFocusVisible,
    entry,
    menu
}) => {
    const {
        handleLongPressEntry,
        handleToggleExpand,
        handlePressEntry,
        customLocalize,
        selectionMode,
        customTheme,
        handleRetry,
        measures
    } = useTreeViewContext();

    const isMenuOpen = useTreeViewSelector((snapshot) => snapshot.menuKey === entry.key);

    const isSelectedKey = useTreeViewSelector((snapshot) => snapshot.selectedKeys.has(entry.key));

    const checkedState = useTreeViewSelector((snapshot) => snapshot.checkedStates.get(entry.key));

    const {
        shapeConfigs,
        typography,
        borders,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const isError = entry.kind === "error";

    const isDisabled = entry.isDisabled;

    const {
        interactionHandlers,
        isPressed
    } = useInteractionState({
        isDisabled
    });

    const {
        isPointerInside,
        rowRef
    } = useTreeViewRowHover();

    const isHovered = !isDisabled && isPointerInside;

    const isSelected = !isError && !isDisabled && selectionMode === "single" && isSelectedKey;

    const currentColors = getTreeViewRowColors({
        isActive: isHovered || isPressed || isMenuOpen,
        isDisabled,
        isSelected,
        isError,
        colors
    });

    const {
        container: containerDynamicStyle,
        label: labelDynamicStyle
    } = useStyles({
        level: entry.level,
        isFocusVisible,
        currentColors,
        shapeConfigs,
        isDisabled,
        isSelected,
        typography,
        measures,
        borders,
        colors
    });

    const isExpandable = entry.isBranch && !entry.isDrillTarget && !isError;

    const handlePress = () => {
        handlePressEntry(entry);
    };

    const handleLongPress = () => {
        handleLongPressEntry(entry);
    };

    const handleToggle = () => {
        handleToggleExpand(entry);
    };

    const handleRetryPress = () => {
        handleRetry(entry);
    };

    const renderToggle = () => {
        return <TreeViewToggle
            isLoading={entry.isLoading && !isError}
            color={currentColors.toggleColor}
            isExpanded={entry.isExpanded}
            isExpandable={isExpandable}
            onPress={handleToggle}
        />;
    };

    const renderCheck = () => {
        if(selectionMode !== "multiple" || isError) {
            return null;
        }

        return <TreeViewCheck
            state={checkedState ?? "unchecked"}
            isDisabled={isDisabled}
        />;
    };

    const renderIcon = () => {
        if(isError) {
            return <View
                style={stylesheet.iconSlot}
                aria-hidden={true}
            >
                <Icon
                    customColor={currentColors.iconColor}
                    customTheme={customTheme}
                    size={measures.iconSize}
                    icon={CircleAlertIcon}
                />
            </View>;
        }

        const IconProp = entry.node.icon;

        if(!IconProp) {
            return null;
        }

        return <View
            style={stylesheet.iconSlot}
            aria-hidden={true}
        >
            <IconProp
                color={isDisabled ? "disabled" : isSelected ? "high" : "mid"}
                customColor={currentColors.iconColor}
                size={measures.iconSize}
            />
        </View>;
    };

    const renderLabel = () => {
        return <Text
            customColor={currentColors.textColor}
            variant={measures.labelVariant}
            customTheme={customTheme}
            style={[
                stylesheet.label,
                labelDynamicStyle
            ]}
            numberOfLines={1}
        >
            {isError ? localize("tree-view-load-failed") : entry.node.title}
        </Text>;
    };

    const renderMeta = () => {
        if(isError || !entry.node.meta) {
            return null;
        }

        return <Text
            customColor={currentColors.metaColor}
            variant={measures.metaVariant}
            customTheme={customTheme}
            style={stylesheet.meta}
            numberOfLines={1}
        >
            {entry.node.meta}
        </Text>;
    };

    const renderAction = () => {
        if(isError || !menu) {
            return null;
        }

        return <TreeViewAction
            isRowActive={isHovered || isFocusVisible}
            entry={entry}
            menu={menu}
        />;
    };

    const renderRetry = () => {
        if(!isError) {
            return null;
        }

        return <Button
            tabIndex={TREE_VIEW_PART_TAB_INDEX}
            title={localize("try-again")}
            onPress={handleRetryPress}
            customTheme={customTheme}
            icon={TreeViewRetryIcon}
            aria-hidden={true}
            focusable={false}
            variant="ghost"
            type="neutral"
            size="small"
        />;
    };

    const renderDrill = () => {
        if(!entry.isDrillTarget) {
            return null;
        }

        return <View
            style={stylesheet.drill}
            aria-hidden={true}
        >
            <Icon
                customColor={currentColors.iconColor}
                color={isSelected ? "high" : "mid"}
                customTheme={customTheme}
                icon={ChevronRightIcon}
                size="small"
            />
        </View>;
    };

    return <Pressable
        {...interactionHandlers}
        onLongPress={menu && !isDisabled ? handleLongPress : undefined}
        tabIndex={TREE_VIEW_PART_TAB_INDEX}
        style={[
            stylesheet.container,
            containerDynamicStyle
        ]}
        onHoverOut={undefined}
        onHoverIn={undefined}
        disabled={isDisabled}
        onPress={handlePress}
        focusable={false}
        ref={rowRef}
        role="none"
    >
        <StateLayer
            isHovered={!isDisabled && (isHovered || isMenuOpen)}
            borderRadius={measures.radius}
            customTheme={customTheme}
            isPressed={isPressed}
        />
        {renderToggle()}
        {renderCheck()}
        {renderIcon()}
        {renderLabel()}
        {renderMeta()}
        {renderRetry()}
        {renderAction()}
        {renderDrill()}
    </Pressable>;
};
export default TreeViewRow;
